/* External */
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

/* Utils */
import useChannelSchedules from './useChannelSchedules';
import { useCurrentTime } from '../providers/CurrentTimeProvider';
import useRefSync from './useRefSync';
import { shallowEqualLists } from '../utils/functions';

export default function useCurrentAndNextSchedules(channel, numberOfSchedules, time) {
  const currentTime = useCurrentTime();
  const schedules = useChannelSchedules(channel);
  const timeToUse = time || currentTime;
  const timeToUseRef = useRefSync(timeToUse);

  const sortedSchedules = useMemo(() => schedules?.sort(
    (scheduleA, scheduleB) => scheduleA.startTime - scheduleB.startTime,
  ), [schedules]);

  const getCurrentAndNextSchedules = useCallback((previous) => {
    let schedulesToReturn = sortedSchedules?.skipUntil(
      ({ startTime, endTime }) => (
        startTime <= timeToUseRef.current && timeToUseRef.current < endTime
      ),
    );

    if (numberOfSchedules) {
      schedulesToReturn = schedulesToReturn?.slice(0, numberOfSchedules);
    }

    if (previous && shallowEqualLists(previous, schedulesToReturn)) {
      return previous;
    }

    return schedulesToReturn;
  }, [sortedSchedules, numberOfSchedules, timeToUseRef]);

  const [
    currentAndNextSchedules,
    setCurrentAndNextSchedules,
  ] = useState(getCurrentAndNextSchedules);

  useEffect(() => {
    const first = currentAndNextSchedules?.first();
    if (
      !first
      || first.channelId !== channel?.channelId
      || first.endTime < timeToUse
      || currentAndNextSchedules?.size < numberOfSchedules
    ) {
      setCurrentAndNextSchedules(getCurrentAndNextSchedules);
    }
  }, [
    getCurrentAndNextSchedules,
    currentAndNextSchedules,
    channel?.channelId,
    timeToUse,
    numberOfSchedules,
  ]);

  return currentAndNextSchedules;
}
